/**
 * DataLake gRPC-Web Service Layer
 * Provides API for Data Curation Pipeline operations with streaming support
 */

import { DataLakeServiceClient } from '../generated/DatalakeServiceClientPb.ts';
import * as datalake_pb from '../generated/datalake_pb.js';

// Envoy proxy URL for DataLake gRPC server
const DATALAKE_GRPC_URL = process.env.REACT_APP_DATALAKE_GRPC_URL || 'http://129.128.215.152:8080';

// Create singleton client instance
const client = new DataLakeServiceClient(DATALAKE_GRPC_URL, null, null);

/**
 * UpdateType enum values from proto
 */
export const UpdateType = {
  UNKNOWN: 0,
  STEP_STARTED: 1,
  STEP_PROGRESS: 2,
  STEP_COMPLETED: 3,
  PIPELINE_COMPLETED: 4,
  PIPELINE_ERROR: 5,
};

/**
 * Helper function to extract step-specific message from update
 */
const getStepMessage = (update: any): string => {
  // Return the appropriate message field based on current step
  if (update.selectImagesMessage) return update.selectImagesMessage;
  if (update.getModelConfigMessage) return update.getModelConfigMessage;
  if (update.registerBatchMessage) return update.registerBatchMessage;
  if (update.bulkPredictMessage) return update.bulkPredictMessage;
  if (update.addAnnotationsMessage) return update.addAnnotationsMessage;
  if (update.weedInstancesMessage) return update.weedInstancesMessage;
  return 'Processing...';
};

/**
 * Step names for display
 */
export const StepNames = {
  1: 'Select Images',
  2: 'Get Model Configuration',
  3: 'Register Annotation Batch',
  4: 'Bulk Predict',
  5: 'Add Annotations to Batch',
  6: 'Extract Weed Instances',
};

/**
 * Start Data Curation Pipeline with streaming updates
 *
 * @param params Pipeline parameters
 * @param onUpdate Callback for each streaming update
 * @param onError Callback for errors
 * @param onComplete Callback when stream completes
 */
export const startPipelineStream = (
  params: {
    creator: string;
    creator_id?: string;
    purpose: string;
    model_name: string;
    uuid?: string;
    is_final: boolean;
    is_original: boolean;
    vendor_name: string;
    annotation_type: string;
    annotation_format: string;
    add_annotations_format: string;
    parent_id?: string;
    weed_threshold_percentage: number;
    min_weed_instance_width?: number;
    min_weed_instance_height?: number;
    source_pipeline_id?: string;
    source_batch_id?: string;
    local_directory_path?: string;
    save_destination?: string;
    select_images_criteria: { [key: string]: string };
  },
  onUpdate: (update: any) => void,
  onError: (err: any) => void,
  onComplete: () => void
) => {
  console.log('[DataLakeGrpcService] Starting pipeline stream with params:', params);

  // Create request
  const request = new datalake_pb.DataCurationPipelineRequest();

  // Set basic fields
  request.setCreator(params.creator);
  if (params.creator_id) request.setCreatorId(params.creator_id);
  request.setPurpose(params.purpose);
  request.setModelName(params.model_name);
  request.setUuid(params.uuid || '');
  request.setIsFinal(params.is_final);
  request.setIsOriginal(params.is_original);
  request.setVendorName(params.vendor_name);
  request.setAnnotationType(params.annotation_type);
  request.setAnnotationFormat(params.annotation_format);
  request.setAddAnnotationsFormat(params.add_annotations_format);
  request.setParentId(params.parent_id || '');
  request.setWeedThresholdPercentage(params.weed_threshold_percentage);

  // Set weed extraction size filters (fields 17-18)
  if (params.min_weed_instance_width) request.setMinWeedInstanceWidth(params.min_weed_instance_width);
  if (params.min_weed_instance_height) request.setMinWeedInstanceHeight(params.min_weed_instance_height);

  // Set partial pipeline fields (fields 19-20)
  if (params.source_pipeline_id) request.setSourcePipelineId(params.source_pipeline_id);
  if (params.source_batch_id) request.setSourceBatchId(params.source_batch_id);

  // Set local directory and save destination (fields 21-22)
  if (params.local_directory_path) request.setLocalDirectoryPath(params.local_directory_path);
  if (params.save_destination) request.setSaveDestination(params.save_destination);

  // Add select images criteria
  Object.entries(params.select_images_criteria).forEach(([key, value]) => {
    if (value) {
      const kv = new datalake_pb.KeyValue();
      kv.setKey(key);
      kv.setValue(value);
      request.addSelectImagesCriteria(kv);
    }
  });

  // Call streaming RPC with extended deadline for long pipelines
  // Production scenarios with 10,000+ images could take hours
  // Set to 24 hours to handle even the longest pipelines
  // grpc-timeout format: https://grpc.io/docs/guides/deadline/
  const metadata = {
    'grpc-timeout': '86400000m' // 24 hours in milliseconds (format: <value><unit> where unit is H/M/S/m/u/n)
  };
  const stream = client.dataCurationPipelineStream(request, metadata);

  stream.on('data', (response: any) => {
    const update = response.toObject();

    // Log different update types with distinct formatting
    if (update.updateType === UpdateType.STEP_PROGRESS) {
      // Keepalive/progress update - highlight these
      console.log(
        `%c⏱️  KEEPALIVE RECEIVED`,
        'color: #4CAF50; font-weight: bold;',
        `Step ${update.currentStep}: ${update.currentStepName}`,
        `| Progress: ${update.progressPercentage}%`,
        `| ${getStepMessage(update)}`
      );
    } else if (update.updateType === UpdateType.STEP_STARTED) {
      console.log(
        `%c🚀 STEP STARTED`,
        'color: #2196F3; font-weight: bold;',
        `Step ${update.currentStep}: ${update.currentStepName}`
      );
    } else if (update.updateType === UpdateType.STEP_COMPLETED) {
      console.log(
        `%c✅ STEP COMPLETED`,
        'color: #4CAF50; font-weight: bold;',
        `Step ${update.currentStep}: ${update.currentStepName}`,
        `| Progress: ${update.progressPercentage}%`
      );
    } else if (update.updateType === UpdateType.PIPELINE_COMPLETED) {
      console.log(
        `%c🎉 PIPELINE COMPLETED`,
        'color: #4CAF50; font-weight: bold; font-size: 14px;',
        `Total duration: ${update.progressPercentage}%`
      );
    } else if (update.updateType === UpdateType.PIPELINE_ERROR) {
      console.error(
        `%c❌ PIPELINE ERROR`,
        'color: #F44336; font-weight: bold;',
        update.errorMessage
      );
    } else {
      // Unknown update type
      console.log('[DataLakeGrpcService] Stream update:', update);
    }

    onUpdate(update);
  });

  stream.on('error', (err: any) => {
    console.error('[DataLakeGrpcService] Stream error:', err);
    onError(err);
  });

  stream.on('end', () => {
    console.log('[DataLakeGrpcService] Stream ended');
    onComplete();
  });

  // Return stream so caller can cancel if needed
  return stream;
};

/**
 * Get queryable attributes (table schema columns) for image selection
 * Returns list of column names that can be used as filter criteria
 */
export const getQueryableAttributes = (): Promise<{ attributesList: string[], success: boolean }> => {
  return new Promise((resolve, reject) => {
    console.log('[DataLakeGrpcService] Getting queryable attributes...');

    // Import Empty from google protobuf
    const { Empty } = require('google-protobuf/google/protobuf/empty_pb');
    const request = new Empty();

    client.getQueryableAttributes(request, {}, (err: any, response: any) => {
      if (err) {
        console.error('[DataLakeGrpcService] Get queryable attributes error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[DataLakeGrpcService] Queryable attributes:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Health check for DataLake server
 */
export const healthCheck = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[DataLakeGrpcService] Checking health...');

    // Import Empty from google protobuf
    const { Empty } = require('google-protobuf/google/protobuf/empty_pb');
    const request = new Empty();

    client.healthCheck(request, {}, (err: any, response: any) => {
      if (err) {
        console.error('[DataLakeGrpcService] Health check error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[DataLakeGrpcService] Health check success:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Get current pipeline status
 */
export const getPipelineStatus = (pipelineId: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[DataLakeGrpcService] Getting pipeline status:', pipelineId);

    const request = new datalake_pb.PipelineStatusRequest();
    request.setPipelineId(pipelineId);

    client.getPipelineStatus(request, {}, (err: any, response: any) => {
      if (err) {
        console.error('[DataLakeGrpcService] Get pipeline status error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[DataLakeGrpcService] Pipeline status:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Watch/resume pipeline with streaming updates
 */
export const watchPipeline = (
  pipelineId: string,
  replayHistory: boolean,
  onUpdate: (update: any) => void,
  onError: (err: any) => void,
  onComplete: () => void
) => {
  console.log('[DataLakeGrpcService] Watching pipeline:', pipelineId, 'replay:', replayHistory);

  const request = new datalake_pb.WatchPipelineRequest();
  request.setPipelineId(pipelineId);
  request.setReplayHistory(replayHistory);

  const metadata = {
    'deadline': String(Date.now() + 86400000) // 24 hours deadline
  };
  const stream = client.watchPipeline(request, metadata);

  stream.on('data', (response: any) => {
    const update = response.toObject();
    console.log('[DataLakeGrpcService] Watch pipeline update:', update);
    onUpdate(update);
  });

  stream.on('error', (err: any) => {
    console.error('[DataLakeGrpcService] Watch pipeline error:', err);
    onError(err);
  });

  stream.on('end', () => {
    console.log('[DataLakeGrpcService] Watch pipeline stream ended');
    onComplete();
  });

  return stream;
};

/**
 * List all active pipelines
 */
export const listActivePipelines = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[DataLakeGrpcService] Listing active pipelines...');

    const { Empty } = require('google-protobuf/google/protobuf/empty_pb');
    const request = new Empty();

    client.listActivePipelines(request, {}, (err: any, response: any) => {
      if (err) {
        console.error('[DataLakeGrpcService] List active pipelines error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[DataLakeGrpcService] Active pipelines:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Get pipeline history with filters
 */
export const getPipelineHistory = (
  creator?: string,
  creatorId?: string,
  startDate?: string,
  endDate?: string,
  limit?: number
): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[DataLakeGrpcService] Getting pipeline history:', { creator, creatorId, startDate, endDate, limit });

    const request = new datalake_pb.PipelineHistoryRequest();
    if (creator) request.setCreator(creator);
    if (creatorId) request.setCreatorId(creatorId);
    if (startDate) request.setStartDate(startDate);
    if (endDate) request.setEndDate(endDate);
    if (limit) request.setLimit(limit);

    client.getPipelineHistory(request, {}, (err: any, response: any) => {
      if (err) {
        console.error('[DataLakeGrpcService] Get pipeline history error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[DataLakeGrpcService] Pipeline history:', result);
        resolve(result);
      }
    });
  });
};

/**
 * Cancel a running pipeline
 */
export const cancelPipeline = (pipelineId: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[DataLakeGrpcService] Cancelling pipeline:', pipelineId);

    const request = new datalake_pb.CancelPipelineRequest();
    request.setPipelineId(pipelineId);

    client.cancelPipeline(request, {}, (err: any, response: any) => {
      if (err) {
        console.error('[DataLakeGrpcService] Cancel pipeline error:', err);
        reject(err);
      } else {
        const result = response.toObject();
        console.log('[DataLakeGrpcService] Pipeline cancelled:', result);
        resolve(result);
      }
    });
  });
};

const datalakeGrpcService = {
  startPipelineStream,
  healthCheck,
  getPipelineStatus,
  watchPipeline,
  listActivePipelines,
  getPipelineHistory,
  cancelPipeline,
  UpdateType,
  StepNames,
};

export default datalakeGrpcService;
